import { appBlocksEn, createFormat, type Formatter } from "@adea/i18n/blocks";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { ColorMap } from "../signature/charts/palette";

/* The words and formats every block and the thread view read. The app mounts `BlocksProvider` once with its live
   copy (`t("blocks")`, `t("thread")` from `app.blocks.*.ts`) and the reader's formatter; anywhere without one (a
   test, a static page) reads English. */

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };
type Deep<T> = T extends string ? string : { [K in keyof T]?: Deep<T[K]> };

export type BlocksCopy = Widen<typeof appBlocksEn>;
export type BlocksCopyIn = Deep<BlocksCopy>;

export type BlocksContext = {
  copy: BlocksCopy;
  format: Formatter;
  /** The company's colours (a value keeps its colour in every chart). */
  colors: ColorMap;
};

const english: BlocksContext = { copy: appBlocksEn, format: createFormat("en"), colors: {} };
const Ctx = createContext<BlocksContext>(english);

function merge<T>(base: T, over: unknown): T {
  if (typeof base === "string" || typeof over !== typeof base || over === null) return (over ?? base) as T;
  const out = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(over as Record<string, unknown>))
    if (v !== undefined) out[k] = k in out ? merge(out[k], v) : v;
  return out as T;
}

export function BlocksProvider({
  copy,
  format,
  colors,
  children,
}: {
  /** The language's words; any missing fall back to English. */
  copy?: BlocksCopyIn | undefined;
  format?: Formatter | undefined;
  colors?: ColorMap | undefined;
  children: ReactNode;
}) {
  const value = useMemo<BlocksContext>(
    () => ({
      copy: copy ? merge(english.copy, copy) : english.copy,
      format: format ?? english.format,
      colors: colors ?? {},
    }),
    [copy, format, colors],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useBlocks = (): BlocksContext => useContext(Ctx);

/** `{{name}}` in a line of copy, filled. */
export function fill(text: string, vars: Record<string, string | number>): string {
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k: string) => String(vars[k] ?? ""));
}
